import React, { useState, useEffect, useRef } from 'react';
import { combineClasses } from '../../utils/formatters';
import authService from '../../services/authService';
import { useNavigationState } from '../../context/navigationContext';
import { 
  TOPBAR_CONFIG, 
  LOCATIONS_CONFIG, 
  USER_MENU_CONFIG, 
  TOPBAR_DEFAULTS,
  TOPBAR_RESPONSIVE 
} from '../../config/topbarConfig';
import {
  TopBarLogo,
  TopBarSpacer,
  TopBarLocation,
  TopBarCart,
  TopBarUserMenu
} from './TopBar/';

// Filter menu items based on authentication status
const getFilteredMenuItems = (isLoggedIn) => {
  return USER_MENU_CONFIG.MENU_ITEMS.filter(item => {
    if (item.showWhen === 'guest') return !isLoggedIn;
    if (item.showWhen === 'authenticated') return isLoggedIn;
    return true; // Show items without showWhen restriction
  });
};

const TopBar = ({ 
  // Display Options
  showLogo = TOPBAR_DEFAULTS.showLogo, 
  showLocation = TOPBAR_DEFAULTS.showLocation, 
  showUserIcon = TOPBAR_DEFAULTS.showUserIcon, 
  showCartIcon = TOPBAR_DEFAULTS.showCartIcon,
  
  // Data Props
  brandName = TOPBAR_CONFIG.BRAND.name,
  currentLocation = TOPBAR_CONFIG.LOCATION.defaultText,
  cartCount = TOPBAR_DEFAULTS.cartCount,
  locations = LOCATIONS_CONFIG.POPULAR_LOCATIONS,
  signupOptions = USER_MENU_CONFIG.SIGNUP_OPTIONS,
  
  // Event Handlers
  onLocationClick = TOPBAR_DEFAULTS.onLocationClick,
  onUserClick = TOPBAR_DEFAULTS.onUserClick,
  onCartClick = TOPBAR_DEFAULTS.onCartClick,
  onSignupUser = TOPBAR_DEFAULTS.onSignupUser,
  onSignupPandit = TOPBAR_DEFAULTS.onSignupPandit,
  onLogoClick = TOPBAR_DEFAULTS.onLogoClick,
  onLogin, // Authentication handler
  onSignup, // Authentication handler
  
  // Styling Props
  className = TOPBAR_DEFAULTS.className,
  containerMaxWidth = TOPBAR_DEFAULTS.containerMaxWidth,
  showBackButton = true,
  
  // User State (for conditional rendering)
  isLoggedIn,
  userInfo
}) => {
  const { canGoBack, goBack, currentPage } = useNavigationState();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [sessionState, setSessionState] = useState(() => ({
    isLoggedIn: authService.isAuthenticated(),
    userInfo: authService.getCurrentUser()
  }));
  const userDropdownRef = useRef(null);
  const locationDropdownRef = useRef(null);

  const isAuthControlled = typeof isLoggedIn === 'boolean' || userInfo !== undefined;
  const resolvedIsLoggedIn = isAuthControlled
    ? Boolean(isLoggedIn)
    : sessionState.isLoggedIn;
  const resolvedUserInfo = isAuthControlled
    ? (userInfo || null)
    : (sessionState.userInfo || null);

  // Get filtered menu items based on login status
  const userMenuItems = getFilteredMenuItems(resolvedIsLoggedIn);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
        setShowLocationDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isAuthControlled) {
      return;
    }

    setSessionState({
      isLoggedIn: authService.isAuthenticated(),
      userInfo: authService.getCurrentUser()
    });
  }, [isAuthControlled]);

  const handleLocationSelect = (location) => {
    setShowLocationDropdown(false);
    if (onLocationClick) {
      onLocationClick(location);
    }
  };

  const handleUserMenuClick = async (action) => {
    setShowUserDropdown(false);
    if (action === 'logout' && !isAuthControlled) {
      await authService.logout();
      setSessionState({ isLoggedIn: false, userInfo: null });
    }

    if (action === 'signup-user' && onSignupUser) {
      onSignupUser();
    } else if (action === 'signup-pandit' && onSignupPandit) {
      onSignupPandit();
    } else if (onUserClick) {
      onUserClick(action);
    }
  };

  const handleLogin = async (loginData) => {
    const result = onLogin
      ? await onLogin(loginData)
      : await authService.login(loginData);

    if (!isAuthControlled) {
      setSessionState({
        isLoggedIn: true,
        userInfo: authService.getCurrentUser()
      });
    }

    return result;
  };

  const handleSignup = async (signupData) => {
    const result = onSignup
      ? await onSignup(signupData)
      : await authService.signup(signupData);

    if (!isAuthControlled) {
      setSessionState({
        isLoggedIn: true,
        userInfo: authService.getCurrentUser()
      });
    }

    return result;
  };

  // Format cart count display
  const formatCartCount = (count) => {
    return count > TOPBAR_CONFIG.CART.maxDisplayCount ? `${TOPBAR_CONFIG.CART.maxDisplayCount}+` : count;
  };

  return (
    <header className={combineClasses(
      TOPBAR_CONFIG.LAYOUT.background,
      TOPBAR_CONFIG.LAYOUT.border,
      TOPBAR_CONFIG.LAYOUT.position,
      TOPBAR_CONFIG.LAYOUT.zIndex,
      className
    )}>
      {/* Top Bar */}
      <div className={combineClasses(
        containerMaxWidth, 
        TOPBAR_CONFIG.COMPONENTS.layout.container,
        TOPBAR_CONFIG.LAYOUT.padding.mobile,
        TOPBAR_CONFIG.LAYOUT.padding.tablet,
        TOPBAR_CONFIG.LAYOUT.padding.desktop
      )}>
        <div className={combineClasses(TOPBAR_CONFIG.COMPONENTS.layout.header, TOPBAR_CONFIG.LAYOUT.height)}>
          
          {/* Logo */}
          <div className="flex items-center gap-3">
            {showBackButton && canGoBack && currentPage !== 'home' && (
              <button
                type="button"
                onClick={goBack}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
                aria-label="Go back"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-xs sm:text-sm font-medium">Back</span>
              </button>
            )}

            <TopBarLogo 
              showLogo={showLogo}
              brandName={brandName}
              onLogoClick={onLogoClick}
            />
          </div>

          {/* Location Selector */}
          <div className="ml-8 md:ml-12">
            <TopBarLocation
              showLocation={showLocation}
              currentLocation={currentLocation}
              showLocationDropdown={showLocationDropdown}
              setShowLocationDropdown={setShowLocationDropdown}
              locations={locations}
              handleLocationSelect={handleLocationSelect}
              locationDropdownRef={locationDropdownRef}
            />
          </div>

          {/* Spacer */}
          <TopBarSpacer />

          {/* Right Side Actions */}
          <div className={combineClasses(TOPBAR_CONFIG.COMPONENTS.layout.actionsContainer, TOPBAR_RESPONSIVE.BREAKPOINTS.SPACING.actions)}>
            {/* User Menu */}
            <TopBarUserMenu
              showUserIcon={showUserIcon}
              showUserDropdown={showUserDropdown}
              setShowUserDropdown={setShowUserDropdown}
              isLoggedIn={resolvedIsLoggedIn}
              userInfo={resolvedUserInfo}
              userMenuItems={userMenuItems}
              handleUserMenuClick={handleUserMenuClick}
              signupOptions={signupOptions}
              userDropdownRef={userDropdownRef}
              onLogin={handleLogin}
              onSignup={handleSignup}
            />

            {/* Cart Icon */}
            <TopBarCart
              showCartIcon={showCartIcon}
              onCartClick={onCartClick}
              cartCount={cartCount}
              formatCartCount={formatCartCount}
            />
          </div>
        </div>
      </div>
    </header>
  );
};

export default TopBar;